import { DESIGN_CATEGORY } from "../constants/designs";
import Element from "../models/furniture/Element";
import LayoutBody from "../models/furniture/LayoutBody";
import LayoutSize from "../models/furniture/LayoutSize";
import Material from "../models/furniture/Material";
import Shape from "../models/furniture/Shape";
import {
  MaterialStep,
  ShapeStep,
} from "../pages/Design/components/DesignsSelector";
import { numberToOrdinal } from "./numbers";

interface ParsedComposition {
  shapes: string[];
  materials: string[][];
}

export const parseComposition = (composition: string): ParsedComposition => {
  const result: ParsedComposition = {
    shapes: [],
    materials: [],
  };

  if (!composition) {
    return result;
  }

  try {
    // Extract shapes part: (SHAPE1,SHAPE2,SHAPE3)
    const shapeMatch = composition.match(/^\(([^)]*)\)/);
    if (shapeMatch) {
      result.shapes = shapeMatch[1]
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s);
    }

    // Extract materials part: ([MAT1,MAT2],[MAT3],[MAT4])
    const materialMatch = composition.match(/\((\[.*\])\)$/);
    if (materialMatch) {
      const materialString = materialMatch[1];
      // Split by ],[ to get individual groups
      const groups = materialString.split(/\],\s*\[/);
      result.materials = groups.map((group) => {
        // Remove brackets and split by comma
        const cleanGroup = group.replace(/^\[|\]$/g, "");
        return cleanGroup
          .split(",")
          .map((m) => m.trim())
          .filter((m) => m);
      });
    }
  } catch (error) {
    console.error("Error parsing composition:", error);

    return { shapes: [], materials: [] };
  }

  return result;
};

export const generateDesignShapesAndMaterialsSteps = (
  elements: Element[] = [],
  composition: string = ""
): [ShapeStep[], MaterialStep[]] => {
  if (elements.length === 0) {
    return [[], []];
  }

  const sortedElements = [...elements].sort((a, b) => a.id - b.id);
  const parsedComposition = parseComposition(composition);

  const shapesSteps: ShapeStep[] = [];

  sortedElements.forEach((element, elementIndex) => {
    const shapeStep: ShapeStep = {
      label: element.name || "Shape",
      data: {
        category: DESIGN_CATEGORY.SHAPE,
        elementId: element.id,
        elementName: element.name || "Shape",
        elementOrder: elementIndex + 1,
      },
    };

    shapesSteps.push(shapeStep);
  });

  const materialsSteps: MaterialStep[] = [];

  if (parsedComposition.materials.length > 0) {
    sortedElements.forEach((element, elementIndex) => {
      const elementMaterials = parsedComposition.materials[elementIndex] || [];

      elementMaterials.forEach((_, materialIndex) => {
        const materialName =
          elementMaterials.length > 1
            ? `${numberToOrdinal(materialIndex + 1)} Material`
            : "Material";

        const materialStep: MaterialStep = {
          label: `${materialName} of ${element.name}`,
          data: {
            category: DESIGN_CATEGORY.MATERIAL,
            materialName: materialName,
            elementId: element.id,
            elementName: element.name || "",
            elementOrder: elementIndex + 1,
            materialOrder: materialIndex + 1,
          },
        };

        materialsSteps.push(materialStep);
      });
    });
  }

  return [shapesSteps, materialsSteps];
};

export function getLayoutSizeFromShapeCode(shapeCode: string): string {
  if (!shapeCode || shapeCode.length < 3) {
    return "";
  }
  return shapeCode.substring(0, 3);
}

export function getLayoutBodyFromShapeCode(shapeCode: string): string {
  if (!shapeCode || shapeCode.length < 6) {
    return "";
  }
  return shapeCode.substring(3, 6);
}

export function isLayoutSizeInComposition(
  composition: string | null,
  layoutSizeCode: string
): boolean {
  if (!composition || !layoutSizeCode) {
    return false;
  }

  const shapes = getShapesFromComposition(composition);
  return shapes.some(
    (shapeCode) => getLayoutSizeFromShapeCode(shapeCode) === layoutSizeCode
  );
}

export function isLayoutBodyInComposition(
  composition: string | null,
  layoutBodyCode: string
): boolean {
  if (!composition || !layoutBodyCode) {
    return false;
  }

  const shapes = getShapesFromComposition(composition);
  return shapes.some(
    (shapeCode) => getLayoutBodyFromShapeCode(shapeCode) === layoutBodyCode
  );
}

export function getLayoutInfoFromComposition(
  composition: string | null
): { layoutSize: string; layoutBody: string } | null {
  if (!composition) {
    return null;
  }

  const shapes = getShapesFromComposition(composition);
  if (shapes.length === 0) {
    return null;
  }

  const firstShape = shapes[0];
  return {
    layoutSize: getLayoutSizeFromShapeCode(firstShape),
    layoutBody: getLayoutBodyFromShapeCode(firstShape),
  };
}

export function isShapeInComposition(
  composition: string | null,
  shapeCode: string
): boolean {
  if (!composition || !shapeCode) {
    return false;
  }

  const shapes = getShapesFromComposition(composition);

  return shapes.includes(shapeCode);
}

export function isMaterialInComposition(
  composition: string | null,
  materialCode: string
): boolean {
  if (!composition || !materialCode) {
    return false;
  }

  const materials = getMaterialsFromComposition(composition);

  return materials.some((materialGroup) =>
    materialGroup.includes(materialCode)
  );
}

export function getShapesFromComposition(composition: string | null): string[] {
  if (!composition) {
    return [];
  }

  const shapesMatch = composition.match(/^\(([^)]*)\)/);

  if (!shapesMatch || !shapesMatch[1]) {
    return [];
  }

  const shapesString = shapesMatch[1];
  return shapesString
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code.length > 0);
}

export function getMaterialsFromComposition(
  composition: string | null
): string[][] {
  if (!composition) {
    return [];
  }

  const materialsMatch = composition.match(/\)\(([^)]*)\)$/);

  if (!materialsMatch || !materialsMatch[1]) {
    return [];
  }

  const materialsString = materialsMatch[1];

  const materialCodes: string[][] = [];
  const materialGroups = materialsString.match(/\[([^\]]*)\]/g);

  if (materialGroups) {
    materialGroups.forEach((group) => {
      const codes = group
        .replace(/[\[\]]/g, "") // Remove brackets
        .split(",")
        .map((code) => code.trim())
        .filter((code) => code.length > 0);

      materialCodes.push(codes);
    });
  }

  return materialCodes;
}

export function hasAnyShapeInComposition(
  composition: string | null,
  shapeCodes: string[]
): boolean {
  if (!composition || !shapeCodes || shapeCodes.length === 0) {
    return false;
  }

  return shapeCodes.some((shapeCode) =>
    isShapeInComposition(composition, shapeCode)
  );
}

export function hasAnyMaterialInComposition(
  composition: string | null,
  materialCodes: string[]
): boolean {
  if (!composition || !materialCodes || materialCodes.length === 0) {
    return false;
  }

  return materialCodes.some((materialCode) =>
    isMaterialInComposition(composition, materialCode)
  );
}

export function isValidCompositionFormat(composition: string | null): boolean {
  if (!composition) {
    return false;
  }

  const compositionPattern = /^\([^)]*\)\([^)]*\)$/;
  return compositionPattern.test(composition);
}

export function checkCompositionIntegrity({
  composition,
  layout_size,
  layout_body,
  shapes,
  materials,
}: {
  composition: string | null;
  layout_size: LayoutSize | null;
  layout_body: LayoutBody | null;
  shapes: Record<number, Shape> | null;
  materials: Record<string, Material> | null;
}): boolean {
  if (!composition || !isValidCompositionFormat(composition)) {
    return false;
  }

  try {
    const compositionShapes = getShapesFromComposition(composition);
    const compositionMaterials = getMaterialsFromComposition(composition);

    const layoutSizeCodes = new Set<string>();
    const layoutBodyCodes = new Set<string>();

    compositionShapes.forEach((shapeCode) => {
      if (shapeCode && shapeCode.length >= 6) {
        layoutSizeCodes.add(getLayoutSizeFromShapeCode(shapeCode));
        layoutBodyCodes.add(getLayoutBodyFromShapeCode(shapeCode));
      }
    });

    for (const layoutSizeCode of layoutSizeCodes) {
      if (!layout_size || !layout_size.code.startsWith(layoutSizeCode)) {
        return false;
      }
    }

    for (const layoutBodyCode of layoutBodyCodes) {
      if (!layout_body || !layout_body.code.startsWith(layoutBodyCode)) {
        return false;
      }
    }

    for (
      let elementOrder = 1;
      elementOrder <= compositionShapes.length;
      elementOrder++
    ) {
      const shapeCode = compositionShapes[elementOrder - 1];
      if (shapeCode) {
        const shape = shapes?.[elementOrder];
        if (!shape || shape.code !== shapeCode) {
          return false;
        }
      }
    }

    if (compositionMaterials.length > 0) {
      for (
        let elementOrder = 1;
        elementOrder <= compositionMaterials.length;
        elementOrder++
      ) {
        const materialCodes = compositionMaterials[elementOrder - 1];
        for (
          let materialOrder = 1;
          materialOrder <= materialCodes.length;
          materialOrder++
        ) {
          const materialCode = materialCodes[materialOrder - 1];
          if (materialCode) {
            const materialKey = `${elementOrder}_${materialOrder}`;
            const material = materials?.[materialKey];

            if (!material || material.code !== materialCode) {
              return false;
            }
          }
        }
      }
    }

    return true;
  } catch (error) {
    return false;
  }
}
